import { h } from 'preact';
import { useEffect, useState, useRef } from 'preact/hooks';
import Panel from 'utils/panel';
import {
  IconClose,
  IconCheckboxRound,
  IconCheckbox2,
  IconCarat,
} from 'utils/icon';
import Image from 'utils/image';
import { formatMoney } from 'utils/storefront/format-resource';

const CartGiftFlyout = ({ giftProduct, cart, isMiniCartEnabled = false }) => {
  const [line, setLine] = useState({});
  const openPanelEvent = new CustomEvent('gift:open');
  const closePanelEvent = new CustomEvent('gift:close');

  const [message, setMessage] = useState('');
  const [messageCount, setMessageCount] = useState(message.length);
  const [selectedGift, setSelectedGift] = useState(null);
  const [error, setError] = useState(false);

  const productList = useRef();
  useEffect(() => {
    const handleGiftOpen = async (event) => {
      setLine(event.detail.line);
      document.dispatchEvent(openPanelEvent);
    };

    window.addEventListener('openGift', handleGiftOpen);

    return () => {
      window.removeEventListener('openGift', handleGiftOpen);
    };
  }, []);

  const handleUpdateOptions = async () => {
    if (!line) return;

    const attributesToKeep = [
      '_monogram_data',
      '_monogram_id',
      '_monogram_price',
      '_monogram_product_id',
      '_tag_:final-sale',
      '_isMonogramFinalSale',
    ];
    const previousAttributes = line.properties || {};
    const giftWrapAttributes = [];
    const hasMessage = message;
    const groupID = crypto.randomUUID();
    const payload = {
      merchandiseId: `${line.variant_id}`,
      quantity: line.quantity,
      attributes: [],
    };

    if (hasMessage) {
      giftWrapAttributes.push({
        key: 'gift message',
        value: message,
      });
      payload.attributes.push({
        key: 'gift message',
        value: message,
      });
    }

    if (selectedGift) {
      giftWrapAttributes.push(
        {
          key: '_domaine_gift_variant_id',
          value: `gid://shopify/ProductVariant/${selectedGift.id}`,
        },
        {
          key: '_gift_product_id',
          value: `gid://shopify/Product/${giftProduct.id}`,
        },
        {
          key: '_gift_price',
          value: `${selectedGift.price / 100}`,
        },
        {
          key: 'gift wrap',
          value: 'true',
        },
        {
          key: '_giftWrapSKU',
          value: `${selectedGift.sku}`,
        },
      );
      payload.attributes.push({
        key: '_giftWrapSKU',
        value: `${selectedGift.sku}`,
      });
      payload.attributes.push({
        key: '_domaine_gift_variant_id',
        value: `${selectedGift.id}`,
      });
    }

    Object.keys(previousAttributes).forEach((key) => {
      if (attributesToKeep.includes(key)) {
        payload.attributes.push({
          key,
          value: previousAttributes[key],
        });
      }
      if (key === '_tag_:final-sale') {
        giftWrapAttributes.push({
          key: '_tag_:final-sale',
          value: previousAttributes[key],
        });
      }
    });

    if (hasMessage || selectedGift) {
      giftWrapAttributes.push({
        key: '_gift',
        value: 'Yes',
      });
      payload.attributes.push({
        key: '_groupID',
        value: groupID,
      });
    }

    if (selectedGift) {
      const giftData = JSON.stringify(giftWrapAttributes);
      payload.attributes.push(
        {
          key: '_gift_data',
          value: giftData,
        },
        { key: '_use_function', value: 'true' },
      );
    } else if (previousAttributes._monogram_data) {
      payload.attributes.push({ key: '_use_function', value: 'true' });
    }

    document.dispatchEvent(closePanelEvent);
    await Promise.all([await cart.remove([line.key]), await cart.add(payload)]);
  };

  useEffect(() => {
    const giftMessageProperty =
      line?.properties && line.properties['gift message']
        ? line.properties['gift message']
        : '';

    setMessage(giftMessageProperty);
  }, [line]);

  useEffect(() => {
    if (line?.properties?._groupID) {
      const giftVariantId = line.properties?._domaine_gift_variant_id;

      if (!giftVariantId) return;
      const product = giftProduct.variants?.find(
        (variant) => `${variant.id}` === giftVariantId,
      );

      setSelectedGift(product);
      if (productList?.current) {
        productList.current.querySelector(
          `[data-id="${product.id}"]`,
        ).checked = true;
      }
    } else {
      setSelectedGift(null);
    }
  }, [line, productList, giftProduct]);

  const handleSelectedGift = (event, product) => {
    if (!event.target.checked) {
      setSelectedGift(null);
      return;
    }
    const products = productList.current.querySelectorAll('input');
    products.forEach((prod) => {
      if (prod.dataset.id === `${product.id}`) return;
      // eslint-disable-next-line no-param-reassign
      prod.checked = false;
    });
    setSelectedGift(product);
  };

  const checkMessageCharacters = (string) => {
    const pattern = /^[ \n'`a-zA-Z0-9_.?!,":-]*$/;
    setError(!pattern.test(string));
  };

  return (
    <Panel
      alignment="right"
      context="gift"
      classes={
        'max-h-none !h-auto lg:max-w-[500px] shadow-[-4px_0px_4px_0px_rgba(0,0,0,0.05)]'
      }
      bodyScrollLock={true}
    >
      <div className="h-full">
        {isMiniCartEnabled ? (
          <button
            onClick={() => document.dispatchEvent(closePanelEvent)}
            className="flex justify-between items-center p-[10px] border-gray-400 border-b bg-white w-full"
            aria-label={window.TMW.Strings.cart.back_to_cart}
          >
            <p className="flex items-center text-xs md:text-sm underline underline-offset-[2.5px] text-default">
              <span className="block rotate-180">
                <IconCarat />
              </span>{' '}
              {window.TMW.Strings.cart.back_to_cart}
            </p>
            <div>
              {' '}
              <IconClose aria-hidden="true" />
            </div>
          </button>
        ) : (
          <div className="flex justify-between p-5 lg:px-10">
            <h3>{window.TMW.Strings.cart.giftTitle}</h3>
            <button onClick={() => document.dispatchEvent(closePanelEvent)}>
              <IconClose />
            </button>
          </div>
        )}
        <div
          className={`h-[calc(100%-146px)] overflow-auto px-5 ${
            !isMiniCartEnabled ? 'lg:px-10' : 'mt-2.5'
          }`}
        >
          {isMiniCartEnabled && (
            <h3 className="mb-2.5 h4">{window.TMW.Strings.cart.giftTitle}</h3>
          )}
          <div
            className={`bg-core-1 px-5 ${
              !isMiniCartEnabled ? 'mb-5 py-4' : 'pt-4 mb-[21px]'
            }`}
          >
            <p className="flex mb-3 items-center cursor-pointer h7--medium w-full text-text-default uppercase gap-2">
              {window.TMW.Strings.cart.giftTextarea}
            </p>
            <textarea
              className="block w-full border-transparent focus:shadow-none focus:ring-transparent h-[120px] focus:border-text-promotion mb-2.5 resize-none bg-white p-2.5 body-2 placeholder:body-2 placeholder:text-text-disabled"
              placeholder={window.TMW.Strings.cart.giftTextarea}
              value={message}
              onInput={(e) => {
                let { value } = e.target;
                if (value.length > 250) value = value.substring(0, 250);
                setMessage(value);
                e.target.value = value;
                setMessageCount(value.length);
                checkMessageCharacters(value);
              }}
            ></textarea>
            <div className="flex justify-between">
              {error && (
                <p className="text-sale body-3">
                  {window.TMW.Strings.cart.messageError}
                </p>
              )}
              <p className="body-3 text-text-subdued flex-grow text-right">
                {messageCount}/250
              </p>
            </div>
          </div>
          <div className="">
            <h3
              className={`text-text-default  ${
                !isMiniCartEnabled ? 'py-3 lg:py-5' : 'pb-3 h4'
              }`}
            >
              {window.TMW.Strings.cart.giftWrapping}
              {'(+'}
              {selectedGift &&
                giftProduct &&
                formatMoney({
                  amount:
                    (selectedGift?.price && selectedGift.price / 100) ||
                    (giftProduct?.price && giftProduct.price / 100),
                })}
              {'):'}
            </h3>
            <div
              className={`bg-core-1 mb-5 lg:mb-7 py-2.5 px-5 ${
                isMiniCartEnabled ? '' : 'lg:pr-16'
              }`}
            >
              <p className="body-3">
                {window.TMW.Strings.cart.giftWrapMessage}
              </p>
              <p className="body-3 text-text-promotion">
                {window.TMW.Strings.cart.giftWrapNote}
              </p>
            </div>

            <ul
              ref={productList}
              className={`${
                isMiniCartEnabled ? 'grid grid-cols-2 gap-2.5 mb-5' : ''
              }`}
            >
              {giftProduct?.variants?.map((variant) => (
                <li
                  key={variant.id}
                  className={`${!isMiniCartEnabled ? 'mb-6 lg:mb-7' : ''}`}
                >
                  <input
                    className="hidden peer"
                    type="checkbox"
                    name="GiftWrap"
                    data-id={variant.id}
                    id={`Wrap-${variant.id}`}
                    onChange={(e) => {
                      handleSelectedGift(e, variant);
                    }}
                    checked={selectedGift?.id === variant.id}
                  />
                  <label
                    className="cursor-pointer h7--medium"
                    htmlFor={`Wrap-${variant.id}`}
                  >
                    <Image
                      src={variant.featured_image.src}
                      alt={variant.featured_image.alt}
                      classes={`w-full object-cover mb-2 lg:mb-3 ${
                        isMiniCartEnabled ? 'h-[126px]' : 'h-[123px]'
                      }`}
                      srcWidths={{ sm: 800, md: 1600 }}
                    />
                    <div className="flex items-center gap-2">
                      {isMiniCartEnabled ? (
                        <IconCheckboxRound
                          checked={variant.id === selectedGift?.id}
                        />
                      ) : (
                        <IconCheckbox2
                          checked={variant.id === selectedGift?.id}
                        />
                      )}
                      {variant.title}
                    </div>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="p-5 bg-core-1">
          <button
            onClick={handleUpdateOptions}
            className={`
              justify-center w-full button button--primary no-underline ${
                isMiniCartEnabled && '!text-12 !leading-160 !tracking-[0.84px]'
              }
            `}
            disabled={error}
          >
            {window.TMW.Strings.cart.giftCta}
          </button>
        </div>
      </div>
    </Panel>
  );
};

export default CartGiftFlyout;
